import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Form, FormButton } from '../../../Common';
import { categoryService } from '../../../../services';
import { adminImageService } from '../../../../services/adminImageService';
import { toast } from 'react-toastify';
import type { OccasionLocale } from '../../../../types/category.types';
import ExperienceFormContent from '../ExperienceFormContent';
import './EditExperienceForm.css';

interface EditExperienceFormProps {
  occasionId: string;
  initialTitle: string;
  initialDescription: string;
  initialImageUrl?: string;
  initialLocale?: OccasionLocale;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const DEFAULT_LANG = 'en';

const EditExperienceForm: React.FC<EditExperienceFormProps> = ({
  occasionId,
  initialTitle,
  initialDescription,
  initialImageUrl,
  initialLocale,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!initialLocale);
  const [uploadedImage, setUploadedImage] = useState<string | null>(initialImageUrl ?? null);
  const [previewImage, setPreviewImage] = useState<string | null>(initialImageUrl ?? null);
  const [currentLang, setCurrentLang] = useState<string>(DEFAULT_LANG);
  const [locale, setLocale] = useState<OccasionLocale>(initialLocale ?? {});

  useEffect(() => {
    if (!initialLocale) {
      let cancelled = false;
      setFetching(true);
      categoryService
        .getExperienceById(occasionId)
        .then((occasion) => {
          if (!cancelled && occasion.locale) {
            setLocale(occasion.locale);
          }
        })
        .catch(() => {
          if (!cancelled) toast.error('Could not load occasion details');
        })
        .finally(() => {
          if (!cancelled) setFetching(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [occasionId, initialLocale]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result as string);
      reader.readAsDataURL(file);
      try {
        const response = await adminImageService.generateUploadUrl({ fileType: file.type });
        await adminImageService.uploadToS3(response.data.uploadUrl, file);
        setUploadedImage(response.data.imageUrl);
      } catch (error) {
        console.error('Failed to process image:', error);
        toast.error('Failed to process image');
        setPreviewImage(null);
      }
    }
  };

  const getDefaultValues = () => {
    const entry = locale[DEFAULT_LANG];
    return {
      title: entry?.title ?? initialTitle,
      description: entry?.description ?? initialDescription,
    };
  };

  const handleSubmit = async (data: { title: string; description: string }) => {
    try {
      setLoading(true);
      if (!data.title.trim()) {
        toast.error('Experience title is required');
        setLoading(false);
        return;
      }
      if (!data.description.trim()) {
        toast.error('Experience description is required');
        setLoading(false);
        return;
      }
      if (!uploadedImage) {
        toast.error('Experience image is required');
        setLoading(false);
        return;
      }

      const finalLocale: OccasionLocale = {
        ...locale,
        [currentLang]: {
          title: data.title.trim(),
          description: data.description.trim(),
        },
      };

      // Default (top-level) title/description must always be English; other locales only live in locale object
      const defaultTitle = finalLocale.en?.title ?? (currentLang === 'en' ? data.title.trim() : initialTitle);
      const defaultDescription = finalLocale.en?.description ?? (currentLang === 'en' ? data.description.trim() : initialDescription);

      await categoryService.updateExperience(occasionId, {
        title: defaultTitle,
        description: defaultDescription,
        imageUrl: uploadedImage,
        locale: Object.keys(finalLocale).length > 0 ? finalLocale : undefined,
      });

      toast.success('Experience updated successfully');
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Failed to update experience:', error);
      const errorMessage =
        error?.response?.data?.message || error?.response?.data?.error || 'Failed to update experience';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Form<{ title: string; description: string }>
      onSubmit={handleSubmit}
      defaultValues={getDefaultValues()}
      resetOnSubmit={false}
    >
      <ExperienceFormContent
        loading={loading}
        previewImage={previewImage}
        onImageUpload={handleImageUpload}
        locale={locale}
        setLocale={setLocale}
        currentLang={currentLang}
        setCurrentLang={setCurrentLang}
        inputId="experience-image-upload-edit"
        contentClassName="add-experience-form-content"
      />
      <Box className="add-experience-form-actions">
        <FormButton type="submit" primary disabled={loading} size="full">
          {loading ? 'Saving...' : 'Save changes'}
        </FormButton>
        <FormButton type="button" primary={false} bordered={false} onClick={onCancel} size="full" disabled={loading}>
          Cancel
        </FormButton>
      </Box>
    </Form>
  );
};

export default EditExperienceForm;


